/**
 * tripRoles.ts — what each stop IS on the trip (Start / Overnight / Via /
 * Destination) and how many nights it holds.
 *
 * The role is derived from position + nights, and the stored `kind` follows
 * from it, so the web planner, the saved trip and the iOS/Android apps all
 * agree (contract in flask-service/trip_routes.py):
 *
 *   first stop            → start        (0 nights)
 *   last stop (≥2 stops)  → end          (0 nights)
 *   middle, 0 nights      → via
 *   middle, ≥1 night      → camp if it's a campsite (entityId), else overnight
 *
 * Legacy kinds: 'waypoint' and 'poi' read as via (old servers stored nights=1
 * on every stop by default, so their nights are ignored).
 */

import type { TripStop, TripStopKind } from '../types/trip';

export type StopRole = 'start' | 'via' | 'overnight' | 'end';

export const MAX_NIGHTS_PER_STOP = 30;

/** Nights a kind implies when the stop doesn't say. */
export function defaultNightsForKind(kind: TripStopKind | string | undefined): number {
  return kind === 'camp' || kind === 'overnight' || kind === 'accom' ? 1 : 0;
}

function clampNights(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(MAX_NIGHTS_PER_STOP, Math.round(n)));
}

/** Nights for a MIDDLE stop (start/end are always 0). */
export function middleStopNights(stop: Pick<TripStop, 'kind' | 'nights'>): number {
  // Legacy kinds: their stored nights are unreliable (old default of 1).
  if (stop.kind === 'waypoint' || stop.kind === 'poi') return 0;
  // An explicit 0 on a camp/overnight means "passing through" — it becomes a via.
  if (typeof stop.nights === 'number') return clampNights(stop.nights);
  return defaultNightsForKind(stop.kind);
}

export function roleForIndex(index: number, count: number, nights: number): StopRole {
  if (index === 0) return 'start';
  if (count >= 2 && index === count - 1) return 'end';
  return nights >= 1 ? 'overnight' : 'via';
}

export function kindForRole(role: StopRole, entityId?: number): TripStopKind {
  if (role === 'overnight') return typeof entityId === 'number' ? 'camp' : 'overnight';
  return role;
}

export function roleLabel(role: StopRole): string {
  switch (role) {
    case 'start':
      return 'Start';
    case 'end':
      return 'Destination';
    case 'overnight':
      return 'Overnight';
    default:
      return 'Passing through';
  }
}

/** The role a stop plays at a given position. */
export function stopRole(stops: TripStop[], index: number): StopRole {
  const stop = stops[index];
  const nights = index === 0 || (stops.length >= 2 && index === stops.length - 1)
    ? 0
    : middleStopNights(stop);
  return roleForIndex(index, stops.length, nights);
}

/**
 * Rewrite kinds + nights to match each stop's position. Returns the SAME array
 * when nothing changes, so it's safe to run from an effect.
 */
export function normaliseTripStops(stops: TripStop[]): TripStop[] {
  let changed = false;
  const next = stops.map((stop, index) => {
    const isEndpoint = index === 0 || (stops.length >= 2 && index === stops.length - 1);
    const nights = isEndpoint ? 0 : middleStopNights(stop);
    const role = roleForIndex(index, stops.length, nights);
    const kind = kindForRole(role, stop.entityId);
    if (stop.kind === kind && stop.nights === nights) return stop;
    changed = true;
    return { ...stop, kind, nights };
  });
  return changed ? next : stops;
}

/** Set a middle stop's nights; the kind follows (0 → via, ≥1 → overnight/camp). */
export function setStopNights(stops: TripStop[], stopId: string, nights: number): TripStop[] {
  const index = stops.findIndex((s) => s.id === stopId);
  if (index <= 0 || index === stops.length - 1) return stops; // endpoints hold 0
  const n = clampNights(nights);
  const updated = stops.map((s, i) => {
    if (i !== index) return s;
    const role: StopRole = n >= 1 ? 'overnight' : 'via';
    return { ...s, nights: n, kind: kindForRole(role, s.entityId) };
  });
  return normaliseTripStops(updated);
}

/** Total nights on the road (sum of nights at every stop). */
export function totalNights(stops: TripStop[]): number {
  return normaliseTripStops(stops).reduce((sum, s) => sum + (s.nights ?? 0), 0);
}

/**
 * Read a stop that came from the API (or an older saved trip) into the
 * planner's shape. Server-side nights for legacy kinds are ignored — old
 * servers defaulted every stop to 1 night.
 */
export function nightsFromApi(kind: string | undefined, nights: number | null | undefined): number {
  if (kind === 'camp' || kind === 'overnight' || kind === 'accom') {
    return typeof nights === 'number' && nights >= 1 ? Math.round(nights) : 1;
  }
  return 0;
}

export function kindFromApi(kind: string | undefined): TripStopKind {
  switch (kind) {
    case 'start':
    case 'end':
    case 'via':
    case 'overnight':
    case 'camp':
    case 'waypoint':
    case 'poi':
      return kind;
    case 'accom':
      return 'camp';
    default:
      return 'camp';
  }
}

